import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node';
import {
  FetcherWithComponents,
  Link,
  useLoaderData,
  useOutletContext,
  useRouteLoaderData,
} from '@remix-run/react';
import {
  ChevronRight,
  Loader2,
  MessageCircle,
  ShoppingBag,
  ShoppingCart,
  Tag,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '~/components/Alert';
import { CartActionGroup } from '~/components/cart/CartActionGroup';
import BulkPricingSavedAmount from '~/components/products/BulkPricingSavedAmount';
import { Price } from '~/components/products/Price';
import { ProductCard } from '~/components/products/ProductCard';
import { ScrollableContainer } from '~/components/products/ScrollableContainer';
import { StockLevelLabel } from '~/components/products/StockLevelLabel';
// import TopReviews from '~/components/products/TopReviews';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/components/tabs/tabs';
import { ProductOrderWidget } from '~/components/products/widget/ProductOrderWidget';
import {
  DetailedProductFragment,
  ErrorCode,
  ErrorResult,
  ProductReviewDetailsFragment,
  SearchQueryVariables,
} from '~/generated/graphql';
import { getProductBySlug, search } from '~/providers/products/products';
// import { getProductReviews } from '~/providers/reviews/reviews';
import { RootLoaderData } from '~/root';
import { CartLoaderData } from '~/routes/api.active-order';
import { getSessionStorage } from '~/sessions';
import { calculateEffectivePrice } from '~/utils/pricing';
import { clearAllSessionData } from '~/utils/auth-helper';
import { useGaEvents } from '~/utils/use-ga-events';

// Meta function for SEO
export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const product = data?.product;

  if (!product) {
    return [{ title: 'Product Not Found - RedTab' }];
  }

  const description = product.description
    ? product.description.replace(/<[^>]*>/g, '').slice(0, 160)
    : `Buy ${product.name} at the best price on RedTab`;

  const image = product.featuredAsset?.preview;
  const price = product.variants?.[0]?.priceWithTax;
  const currency = product.variants?.[0]?.currencyCode;

  return [
    { title: `${product.name} - RedTab` },
    { name: 'description', content: description },
    { property: 'og:title', content: product.name },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'product' },
    ...(image ? [{ property: 'og:image', content: image }] : []),
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: product.name },
    { name: 'twitter:description', content: description },
    ...(image ? [{ name: 'twitter:image', content: image }] : []),
    // JSON-LD structured data for rich snippets
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: description,
        ...(image && { image }),
        ...(price &&
          currency && {
            offers: {
              '@type': 'Offer',
              price: price / 100,
              priceCurrency: currency,
              availability:
                product.variants?.[0]?.stockLevel === 'OUT_OF_STOCK'
                  ? 'https://schema.org/OutOfStock'
                  : 'https://schema.org/InStock',
            },
          }),
      },
    },
  ];
};

// Loader with session storage for cart errors
export async function loader({ params, request }: LoaderFunctionArgs) {
  const { slug } = params;

  const searchVariables: SearchQueryVariables = { input: { take: 12 } };

  // Parallel fetch product and related products
  const [productResponse, searchResponse] = await Promise.all([
    getProductBySlug(slug!, { request }),
    search(searchVariables, { request }),
  ]);

  const product = productResponse.product;
  const allProducts = searchResponse.search.items || [];

  // Fetch reviews with error handling
  let reviews: ProductReviewDetailsFragment[] = [];
  // Temporarily disabled reviews fetching
  // if (product?.id) {
  //   try {
  //     const reviewsResponse = await getProductReviews(
  //       {
  //         options: {
  //           filter: { productId: { eq: product.id } },
  //           sort: { createdAt: SortOrder.Desc },
  //         },
  //       },
  //       { request }
  //     );
  //     reviews = reviewsResponse.productReviews?.items || [];
  //   } catch (error) {
  //     console.error(`[Product Loader] Failed to fetch reviews for product: ${product.id}`, error);
  //     // Continue even if reviews fail - not critical
  //   }
  // }

  // Get session storage for cart errors
  const sessionStorage = await getSessionStorage();
  const session = await sessionStorage.getSession(
    request?.headers.get('Cookie'),
  );
  const cartError = session.get('activeOrderError');

  return json(
    { product, allProducts, reviews, error: cartError },
    {
      headers: {
        'Set-Cookie': await sessionStorage.commitSession(session),
        'Cache-Control': 'public, max-age=60, stale-while-revalidate=300',
      },
    },
  );
}

// Error handling function for cart operations
function getAddItemToOrderError(
  error?: ErrorResult | string,
): string | undefined {
  if (!error) return undefined;

  if (typeof error === 'string') return error;

  if (!error.errorCode) return undefined;

  switch (error.errorCode) {
    case ErrorCode.OrderModificationError:
    case ErrorCode.OrderLimitError:
    case ErrorCode.NegativeQuantityError:
    case ErrorCode.InsufficientStockError:
      return error.message;
    default:
      return undefined;
  }
}

// Normalize price helper
function normalizePrice(
  price:
    | number
    | { value: number }
    | { min: number; max: number }
    | { __typename?: 'SinglePrice'; value: number }
    | { __typename?: 'PriceRange'; min: number; max: number }
    | null
    | undefined,
):
  | { __typename?: 'SinglePrice'; value: number }
  | { __typename?: 'PriceRange'; min: number; max: number }
  | undefined {
  if (price == null) return undefined;
  if (typeof price === 'number')
    return { __typename: 'SinglePrice', value: price };
  if ('value' in price && typeof price.value === 'number')
    return { __typename: 'SinglePrice', value: price.value };
  if ('min' in price && 'max' in price)
    return { __typename: 'PriceRange', min: price.min, max: price.max };
  return undefined;
}

// Calculate discount
function calculateDiscount(
  originalPrice: number,
  currentPrice: number,
): number {
  if (!originalPrice || !currentPrice || originalPrice <= currentPrice)
    return 0;
  return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
}

// Breadcrumb component
function Breadcrumb({ product }: { product: DetailedProductFragment }) {
  const breadcrumbs = [
    { name: 'Home', href: '/' },
    {
      name: product?.collections?.[0]?.name || 'Products',
      href: product?.collections?.[0]?.slug
        ? `/collections/${product.collections[0].slug}`
        : '/search',
    },
    { name: product?.name, href: '#', current: true },
  ];

  return (
    <nav className="flex mb-4 sm:mb-6 overflow-x-auto" aria-label="Breadcrumb">
      <ol className="flex items-center text-[13px] leading-[16px] tracking-[0em] font-normal text-[#54454A]">
        {breadcrumbs.map((item, index) => (
          <li key={item.name} className="flex-shrink-0">
            <div className="flex items-center">
              {index > 0 && (
                <span className="mx-2">
                  <ChevronRight size={14} color="#54454A" />
                </span>
              )}
              {item.current ? (
                <span className="truncate max-w-[200px] sm:max-w-md">
                  {item.name}
                </span>
              ) : (
                <Link
                  to={item.href}
                  className="hover:text-[#1A090E] transition-colors truncate max-w-[150px] sm:max-w-none"
                >
                  {item.name}
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Product badges component
function ProductBadges({ product }: { product: DetailedProductFragment }) {
  const badges = [];

  if (
    product?.facetValues?.some((fv) =>
      fv.facet?.name?.toLowerCase().includes('brand'),
    )
  ) {
    const brandFacet = product.facetValues.find((fv) =>
      fv.facet?.name?.toLowerCase().includes('brand'),
    );
    if (brandFacet) {
      badges.push({
        text: brandFacet.name,
        className: 'bg-blue-100 text-blue-800 border-blue-200',
      });
    }
  }

  const mainPrice = normalizePrice(product.variants?.[0]?.priceWithTax);
  const originalPrice = product.variants?.[0]?.priceWithTax;
  if (mainPrice?.__typename === 'SinglePrice' && originalPrice) {
    const discount = calculateDiscount(originalPrice, mainPrice.value);
    if (discount > 0) {
      badges.push({
        text: `${discount}% OFF`,
        className: 'bg-primary text-white border-primar/20',
      });
    }
  }

  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {badges.map((badge, index) => (
        <span
          key={`badge-${index}`}
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${badge.className}`}
        >
          {badge.text}
        </span>
      ))}
    </div>
  );
}

// Price display with cart integration
function EnhancedPriceDisplay({
  selectedVariant,
  quantity = 1,
}: {
  selectedVariant: DetailedProductFragment['variants'][number] | undefined;
  quantity?: number;
}) {
  const baseUnitPrice = normalizePrice(selectedVariant?.priceWithTax);
  const currencyCode = selectedVariant?.currencyCode;

  // Calculate effective price with tier pricing using utility
  const basePrice =
    baseUnitPrice?.__typename === 'SinglePrice' ? baseUnitPrice.value : 0;
  const { effectiveUnitPrice: tierPrice, activeTier: activeBulkTier } =
    calculateEffectivePrice(
      basePrice,
      selectedVariant?.customFields?.priceTiers,
      quantity,
    );

  // Use the effective unit price from utility
  const effectiveUnitPrice = tierPrice;

  // Original price is either the custom field or the base price
  const customOriginalPrice = selectedVariant?.customFields?.originalPrice;

  const originalUnitPrice = customOriginalPrice
    ? customOriginalPrice
    : baseUnitPrice?.__typename === 'SinglePrice'
    ? baseUnitPrice.value
    : undefined;

  // Calculate total price based on quantity and normalize it
  const mainPrice = normalizePrice({ value: effectiveUnitPrice * quantity });

  const originalPrice = originalUnitPrice
    ? originalUnitPrice * quantity
    : undefined;

  // Daily installment calculation (normalized)
  const dailyInstallment =
    mainPrice?.__typename === 'SinglePrice'
      ? normalizePrice({ value: Math.round(mainPrice.value / 60) })
      : undefined;

  // Calculate discount and savings
  const discount =
    originalPrice &&
    mainPrice?.__typename === 'SinglePrice' &&
    originalPrice > mainPrice.value
      ? calculateDiscount(originalPrice, mainPrice.value)
      : 0;

  const savings =
    originalPrice &&
    mainPrice?.__typename === 'SinglePrice' &&
    originalPrice > mainPrice.value
      ? originalPrice - mainPrice.value
      : 0;

  if (!mainPrice || !currencyCode) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-3">
        <div className="text-2xl font-bold text-primary">
          <Price priceWithTax={mainPrice} currencyCode={currencyCode} />
        </div>

        {originalPrice && discount > 0 && (
          <span className="text-lg text-gray-500 line-through">
            <Price
              priceWithTax={{ __typename: 'SinglePrice', value: originalPrice }}
              currencyCode={currencyCode}
            />
          </span>
        )}
      </div>

      {savings > 0 && mainPrice?.__typename === 'SinglePrice' && (
        <p className="text-sm text-gray-600 font-medium">
          You save{' '}
          <span className="text-primary font-bold">
            <Price
              priceWithTax={{ __typename: 'SinglePrice', value: savings }}
              currencyCode={currencyCode}
            />
            {discount > 0 && ` (${discount}%)`}
          </span>
          {activeBulkTier ? ' with bulk pricing' : ''}
        </p>
      )}

      {dailyInstallment && (
        <div className="bg-primary/5 border border-primary/30 rounded-md p-2 sm:p-3">
          <p className="text-xs sm:text-sm font-medium text-primary/90 mb-0.5 sm:mb-1">
            Buy Big, Pay Small
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
            <span className="text-sm sm:text-lg font-bold text-primary">
              Daily:{' '}
              <Price
                priceWithTax={dailyInstallment}
                currencyCode={currencyCode}
              />
            </span>
          </div>
          <p className="text-[10px] sm:text-xs text-primary/80 mt-0.5 sm:mt-1 leading-tight">
            Pay just{' '}
            {dailyInstallment?.__typename === 'SinglePrice' &&
            dailyInstallment.value != null ? (
              <Price
                priceWithTax={dailyInstallment}
                currencyCode={currencyCode}
              />
            ) : (
              '₹0'
            )}{' '}
            daily for 60 days. No interest.
          </p>
        </div>
      )}

      {/* Stock Level */}

      {/* //? I removed the model from the bottom since it in the above */}
      {/* <div className="flex items-center space-x-2">
        <span className="text-gray-500 text-sm">{selectedVariant?.sku}</span>
        <StockLevelLabel stockLevel={selectedVariant?.stockLevel} />
      </div> */}
    </div>
  );
}

// Specifications tab

// Main ProductSlug component
export default function ProductSlug() {
  const { t } = useTranslation();
  const {
    product,
    allProducts = [],
    reviews = [],
    error,
  } = useLoaderData<typeof loader>();

  // Get cart context
  const { activeOrderFetcher } = useOutletContext<{
    activeOrderFetcher: FetcherWithComponents<CartLoaderData>;
  }>();
  const { activeOrder } = activeOrderFetcher.data ?? {};
  const isSubmitting = activeOrderFetcher.state !== 'idle';

  // Get root loader data for auth status
  const rootData = useRouteLoaderData<RootLoaderData>('root');

  const isAuthenticated = !!rootData?.activeCustomer?.activeCustomer?.id;

  const { trackViewItem } = useGaEvents();

  useEffect(() => {
    if (product) {
      trackViewItem(product);
    }
  }, [product, trackViewItem]);

  // State management
  const [tabValue, setTabValue] = useState('description');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(
    product?.variants?.[0]?.id || '',
  );

  // Error handling
  if (typeof error === 'string') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">😞</div>
          <h1 className="text-2xl font-bold mb-2 text-gray-900">
            Oops! Something went wrong
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-primary/40 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  if (!product) return null;

  // Helper functions
  const findVariantById = (id: string) =>
    product.variants.find((v) => v.id === id);

  const selectedVariant = findVariantById(selectedVariantId);
  if (!selectedVariant && product.variants.length > 0) {
    setSelectedVariantId(product.variants[0].id);
  }

  const addItemToOrderError = getAddItemToOrderError(
    typeof error === 'object' ? error : undefined,
  );

  const [quantity, setQuantity] = useState(1);

  // Get cart line for the selected variant (for reference, if needed)
  const cartLine = activeOrder?.lines.find(
    (l) => l.productVariant.id === selectedVariantId,
  );

  const qtyInCart = cartLine?.quantity ?? 0;

  // Sync local quantity with cart quantity when variant changes or cart updates
  // REMOVED: Syncing causes confusion (Add vs Update). Default to 1.
  useEffect(() => {
    setQuantity(1);
  }, [selectedVariantId]);

  const productImages = [
    selectedVariant?.featuredAsset?.preview || product.featuredAsset?.preview,
    ...(product.assets?.map((asset) => asset.preview) || []),
  ].filter(Boolean);

  const whatsappMessage = `Hi! I'm interested in ${product.name}${
    selectedVariant?.sku ? ` (Model: ${selectedVariant.sku})` : ''
  }. Can you provide more details?`;

  // Cart action handlers for CartActionGroup
  const handleAdjustOrderLine = (lineId: string, newQuantity: number) => {
    setQuantity(newQuantity);
  };

  // Determine if we're adding to cart or updating quantity
  const isItemInCart = qtyInCart > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 md:px-6 lg:px-8 xl:px-8 py-8">
        <Breadcrumb product={product} />

        {/* Product Main Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 relative">
          {/* 1. Left Side: Product Image Section */}
          <div className="lg:col-span-4 space-y-6 order-1">
            {/* Product Images */}
            <div className="space-y-4">
              <div className="overflow-hidden rounded-3xl bg-white p-2 border border-gray-100 shadow-sm">
                <div className="aspect-square w-full max-h-[600px] flex items-center justify-center relative">
                  {productImages.length > 0 ? (
                    <img
                      alt={product.name}
                      className="object-contain w-full h-full transition-transform duration-500 hover:scale-105"
                      src={
                        productImages[selectedImageIndex] || '/placeholder.svg'
                      }
                    />
                  ) : (
                    <div className="w-full h-full relative overflow-hidden bg-transparent">
                      <img
                        src="/brand_logo.png"
                        alt="Logo thumbnail"
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                        loading="lazy"
                      />
                      <span className="sr-only">
                        No product image available
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Thumbnail Images */}
              {productImages.length > 1 && (
                <ScrollableContainer>
                  {[...new Set(productImages)]
                    .filter((img): img is string => img != null)
                    .map((image, index) => (
                      <div
                        key={`Image-${index}`}
                        className={`basis-1/3 md:basis-1/4 flex-shrink-0 select-none touch-pan-x rounded-md cursor-pointer ${
                          selectedImageIndex === index
                            ? 'outline outline-2 outline-primary outline-offset-[-2px]'
                            : 'border border-gray-200 hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedImageIndex(index)}
                      >
                        <img
                          draggable="false"
                          className="rounded-md select-none h-24 w-full object-cover"
                          src={image + '?preset=full'}
                        />
                      </div>
                    ))}
                </ScrollableContainer>
              )}
            </div>
          </div>

          {/* 2. Middle: Product Information Section [Description/Specification] */}
          <div className="lg:col-span-4 space-y-6 order-3 lg:order-2">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 space-y-6">
              <ProductBadges product={product} />

              <div className="flex flex-col gap-2">
                <h1 className="text-2xl lg:text-3xl font-extrabold text-gray-900 leading-tight">
                  {product.name}
                </h1>
                {(product as any).supplier?.name && (
                  <p className="text-sm font-medium text-gray-500">
                    Sold by {(product as any).supplier.name}
                  </p>
                )}
                {/* SKU & Stock Info */}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <StockLevelLabel stockLevel={selectedVariant?.stockLevel} />
                  {selectedVariant?.sku && (
                    <span>
                      Model:{' '}
                      <span className="font-semibold text-gray-900">
                        {selectedVariant.sku}
                      </span>
                    </span>
                  )}
                </div>
              </div>

              {/* Tabs for Description & Specs */}
              <Tabs
                defaultValue="description"
                className="w-full mt-6"
                value={tabValue}
                onValueChange={setTabValue}
              >
                <TabsList className="flex w-full justify-start border-b border-gray-200 bg-transparent p-0 mb-6 rounded-none gap-8 h-auto">
                  <TabsTrigger
                    value="description"
                    className={`pb-3 px-0 text-base font-semibold rounded-none border-b-2 transition-all bg-transparent shadow-none ${
                      tabValue === 'description'
                        ? 'border-[#E80245] text-[#E80245]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Description
                  </TabsTrigger>
                  <TabsTrigger
                    value="specifications"
                    className={`pb-3 px-0 text-base font-semibold rounded-none border-b-2 transition-all bg-transparent shadow-none ${
                      tabValue === 'specifications'
                        ? 'border-[#E80245] text-[#E80245]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Specifications
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="description"
                  className="prose max-w-none text-gray-600 text-sm leading-relaxed"
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: product.description || '',
                    }}
                  />
                </TabsContent>
                <TabsContent value="specifications" className="mt-4">
                  {/* Simplified Specs View */}
                  <div className="grid grid-cols-1 gap-3">
                    {product.facetValues?.map((fv) => (
                      <div
                        key={fv.id}
                        className="flex flex-col p-3 bg-gray-50 rounded-xl border border-gray-100"
                      >
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          {fv.facet.name}
                        </span>
                        <span className="font-semibold text-gray-900 text-sm">
                          {fv.name}
                        </span>
                      </div>
                    ))}
                    {(!product.facetValues ||
                      product.facetValues.length === 0) && (
                      <div className="text-sm text-gray-500 italic p-4 text-center">
                        No specifications available for this product.
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* 3. Right Side: Ordering System Section */}
          <div className="lg:col-span-4 order-2 lg:order-3 relative">
            <div className="sticky top-24">
              {selectedVariant && (
                <ProductOrderWidget
                  product={product}
                  selectedVariant={selectedVariant}
                  quantity={quantity}
                  setQuantity={setQuantity}
                  activeOrderFetcher={activeOrderFetcher}
                  isItemInCart={isItemInCart}
                  isSubmitting={isSubmitting}
                  variants={product.variants}
                  onVariantChange={(id) => {
                    setSelectedVariantId(id);
                    const variant = findVariantById(id);
                    if (variant?.featuredAsset) {
                      setSelectedImageIndex(0);
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 md:px-6 lg:px-8 xl:px-8">
        {/* Related Products Section */}
        {allProducts.length > 0 && (
          <div className="bg-white rounded-3xl p-6 border border-gray-100 mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2
                style={{
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '28px',
                  letterSpacing: '-0.02em',
                  color: '#1A090E',
                }}
              >
                Related Product
              </h2>
              <Link
                to={
                  product.collections?.[0]?.slug
                    ? `/collections/${product.collections[0].slug}`
                    : '/search'
                }
                className="font-semibold text-[14px] leading-[24px] tracking-[0em] underline text-[#000000]"
              >
                View all
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {allProducts.slice(0, 12).map((prod) => (
                <ProductCard
                  key={prod.productId}
                  productAsset={prod.productAsset}
                  productName={prod.productName}
                  description={prod.description}
                  slug={prod.slug}
                  priceWithTax={prod.priceWithTax}
                  currencyCode={prod.currencyCode}
                  productId={prod.productId}
                  productVariantId={prod.productVariantId}
                  productVariantName={prod.productVariantName}
                  productVariant={prod.productVariant}
                  sku={prod.sku}
                  inStock={prod.inStock}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Export shouldRevalidate function for proper cart state management
export const shouldRevalidate = () => true;
